import React, { useState, useContext } from 'react'
import { AdminContext } from '../../contexts/AdminContext'
import { useForm } from '@mantine/form'
import { changePassword } from '../../services/auth'
import { Tabs, TabList, TabIndicator, Text, Box, TabPanels, Tab, TabPanel, VStack, useToast, Button } from '@chakra-ui/react'
import ChangePasswordForm from '../forms/ChangePasswordForm'
import { SuccessToast } from '../alert/SuccessToast'
import AdminCard, { AdminCardSkeleton } from './AdminCard'
import { useQuery } from 'react-query'
import { getAdmins } from '../../services/admin'

export interface PasswordType { 
  current_password: string,
  new_password: string
}

const ProfileTab: React.FC = () => {
  const admins = useQuery('admins', () => getAdmins(token))
  const { admin } = useContext(AdminContext)
  const toast = useToast()
  const token = `bearer ${admin.token}`
  const [isLoading, setIsLoading] = useState({
    changePassword: false,
    admins: false
  })

  const changePasswordForm = useForm({
    initialValues: { 
      current_password: '',
      new_password: '',
    },

    validate: {
      current_password: (value) => (
        !value ? 'Current password is required' : null
      ),
      new_password: (value) => (
        !value ? 'New password is required' :
          value.length < 6 ? 'New password is less than 6 characters' : null
      )
    },
  });

  const handleChangePassword = async (values: PasswordType) => {
    setIsLoading({
      ...isLoading,
      changePassword: true
    })

    const payload = {
      ...values,
      email: admin.email
    }

    try{
      await changePassword(payload)
      changePasswordForm.reset()

      setIsLoading({
        ...isLoading,
        changePassword: false
      })

      toast({
        title: "Password Updated!",
        status: 'success',
        isClosable: true,
        duration: 2000,
        render: () => (
          <SuccessToast 
            message="Password Updated!" 
          />
        )
      })
    } catch (error: any) {
      setIsLoading({
        ...isLoading,
        changePassword: false
      })

      changePasswordForm.setErrors({
        current_password: error.response.data.message
      })
    }
  }
  return (
    <Tabs>
      <TabList color='gray.500'>
        <Tab _selected={{ color: '#37A75B' }}>
          <Text fontSize='md' fontWeight='semibold'>
            Profile
          </Text>
        </Tab>

        <Tab _selected={{ color: '#37A75B' }}>
          <Text fontSize='md' fontWeight='semibold'>
            Change Password
          </Text>
        </Tab>

        {admin.role === 'super' && 
          <Tab _selected={{ color: '#37A75B' }}>
            <Text fontSize='md' fontWeight='semibold'>
              Admin Lists
            </Text>
          </Tab>
        }
      </TabList>

      <TabIndicator
        mt="-1.5px"
        height="2px"
        bg="#37A75B"
        borderRadius="1px"
      />

      <TabPanels>
        <TabPanel p={0}>
          <VStack 
            pt='8' pb='10' px='7' boxShadow='md' borderRadius='8' maxW='40rem' w='100%' mt='10' spacing='5'
          >
            <Box w='100%'>
              <Text fontWeight='semibold' color='gray.500'>
                First Name
              </Text>

              <Box mt='2'>
                <Text
                  p='2' border='1px' borderRadius='5' borderColor='gray.500' color='gray.500'
                  fontWeight='semibold'
                >
                  {admin.firstName}
                </Text>
              </Box>
            </Box>

            <Box w='100%'>
              <Text fontWeight='semibold' color='gray.500'>
                Last Name
              </Text>

              <Box mt='2'>
                <Text
                  p='2' border='1px' borderRadius='5' borderColor='gray.500' color='gray.500'
                  fontWeight='semibold'
                >
                  {admin.lastName}
                </Text>
              </Box>
            </Box>

            <Box w='100%'>
              <Text fontWeight='semibold' color='gray.500'>
                Email Address
              </Text>

              <Box mt='2'>
                <Text
                  p='2' border='1px' borderRadius='5' borderColor='gray.500' color='gray.500'
                  fontWeight='semibold'
                >
                  {admin.email}
                </Text>
              </Box>
            </Box>

            <Box w='100%'>
              <Text fontWeight='semibold' color='gray.500'>
                Role
              </Text>

              <Box mt='2'>
                <Text
                  p='2' border='1px' borderRadius='5' borderColor='gray.500' color='gray.500'
                  fontWeight='semibold'
                >
                  {admin.role}
                </Text>
              </Box>
            </Box>
          </VStack>
        </TabPanel>

        <TabPanel p={0}>
          <VStack
            pt='8' pb='10' px='7' boxShadow='md' borderRadius='8' maxW='40rem' w='100%' 
            mt='10' spacing='5'
          >
            <ChangePasswordForm
              isLoading={isLoading}
              changePasswordForm={changePasswordForm}
              handleChangePassword={handleChangePassword}
            />
          </VStack>
        </TabPanel>

        <TabPanel p={0}>
          <VStack
            pt='8' pb='10' px='7' boxShadow='md' borderRadius='8' maxW='40rem' maxH='42rem' w='100%' overflowY='scroll'
            mt='10' spacing='5'
          >
            {admins.data &&
              admins.data.map((admin: any, i: any) => (
                <AdminCard key={i} admin={admin} />
              ))

            }

            {admins.isLoading &&
              <AdminCardSkeleton />
            }

            {admins.isError &&
              <Box textAlign='center'>
                <Text
                  p='2' color='red.500'
                  fontWeight='semibold'
                >
                  Failed to fetch Admins
                </Text>

                <Button colorScheme='green' size='lg' onClick={() => admins.refetch()}>
                  Try again
                </Button>
              </Box>
            }

            {admins.data &&
              admins.data.length < 1 &&
              <Box textAlign='center'>
                <Text
                  p='2' color='gray.500'
                  fontWeight='semibold'
                >
                  You have not registered other admins yet
                </Text>
              </Box>
            }
          </VStack>
        </TabPanel>
      </TabPanels>
    </Tabs>
  )
}

export default ProfileTab